import { forwardRef, useCallback, useEffect, useRef, useState } from 'react'
import { useBreakpoints, usePublish, useView } from '@10000/react'
import { average } from '@10000/common'
import { useScale } from '@10000/react/src/widgets'
import { Body, Box, CenteredBox, ElementHighlight, Expertise, H4, PracticePie, TooltipSeries } from '../widgets'
import { PracticeComparisonCard } from './PracticeComparisonCard.jsx'
import { Controls, ResetTooltips, useAnalyticsData } from '.'
import { assetUrl, practiceAverages } from '../../utilities.js'
import { CompareDialogs } from './guideDialogs'
import './Compare.scss'

export const Compare = ({ displayMode, ...props }) => {
  const publish = usePublish('squiz')
  const [initial, setInitial] = useState(true)
  const { options: { guides } = {}, analyticsGuidesCompleted: { compare } = {}, presenter } = useView('squiz')
  const { setPractice, setFilters } = useAnalyticsData()
  const [guideActive, setGuideActive] = useState(false)

  const completeTutorial = () => {
    if(presenter) {
      publish('analyticsGuideCompleted', { guide: 'compare' })
    } else {
      setGuideActive(false)
    }
  }

  const resetCompareGuide = useCallback(() => {
    setPractice(undefined)
    setFilters({ compareConsensus: 0 })
    if(!presenter) {
      setGuideActive(true)
    }
  }, [setPractice, setFilters, presenter])

  useEffect(() => {
    if(initial && guides && !compare) {
      setInitial(false)
      resetCompareGuide()
    }
  }, [initial, guides, compare, resetCompareGuide])

  return (
    <TooltipSeries
      complete={(!guides || compare) && !guideActive}
      onComplete={completeTutorial}
      onReset={resetCompareGuide}
      active={!displayMode}
    >
      <CompareDialogs />
      <CompareDashboard displayMode={displayMode} {...props} />
    </TooltipSeries>
  )
}

export const CompareDashboard = ({ displayMode, ...props }) => {
  const { latest, practice, setPractice, filters: { compareConsensus }, compare } = useAnalyticsData()
  const averages = latest.teamAverage.map(average)
  const values = practiceAverages(averages)
  const pie = useRef()
  const { mobile } = useBreakpoints()

  const comparisonValues = compareConsensus ? practiceAverages(latest.consensus.map(x => x?.answer)) : compare
  const comparisonVisible = practice !== undefined

  return (
    <>
      <Controls displayMode={displayMode} />

      <Box
        container
        grow
        fullWidth
        maxWidth="100vw"
        justifyContent="space-around"
        gap={16}
        className="comparison"
        style={{ containerType: 'size' }}
        mt={24}
        px={12}
        {...props}
      >
        <Target
          title={compareConsensus ? 'Standard' : 'Yours'}
          values={values}
          onPracticeSelected={displayMode ? undefined : setPractice}
          selectedPractice={practice}
          transformOrigin="right"
          ref={pie}
        />
        <Box minWidth={250} fullHeight justifyContent="center" className="desktop-only">
          {comparisonVisible &&
            <Results
              yours={values[practice]}
              yourChange={undefined}
              comparison={comparisonValues?.[practice]}
              comparisonChange={undefined}
              consensus={compareConsensus}
            />
          }
        </Box>
        <Target
          title={compareConsensus ? 'Consensus' : 'Global average'}
          values={comparisonValues}
          onPracticeSelected={displayMode ? undefined : setPractice}
          selectedPractice={practice}
          transformOrigin="left"
        />

        <CenteredBox
          position="fixed"
          inset
          className="mobile-only"
          opacity={comparisonVisible ? 1 : 0}
          zIndex={comparisonVisible ? 1 : -1}
          transition="0.5s"
        >
          <PracticeComparisonCard
            practice={practice}
            yours={values[practice]}
            yourChange={undefined}
            comparison={comparisonValues?.[practice]}
            comparisonChange={undefined}
            onClose={() => setPractice(undefined)}
            displayMode={displayMode}
          />
        </CenteredBox>
      </Box>

      {!displayMode && <ResetTooltips guide="compare" />}

      <ElementHighlight
        circle
        target={pie}
        order={3}
        pointDirection={mobile ? 'down' : 'left-down'}
        nextOnFinish
      />
    </>
  )
}

const Results = ({ yours, comparison, yourChange, comparisonChange, consensus }) => (
  <Box column fullHeight justifyContent="space-around" className="desktop-only fuck">
    {(!isNaN(yours) && yours !== null && yours !== undefined) && <Result value={yours} change={yourChange} title={consensus ? 'STANDARD MODE' : 'YOUR RESULT'} />}
    {(!isNaN(comparison) && comparison !== null && comparison !== undefined) && <Result value={comparison} change={comparisonChange} title={consensus ? 'CONSENSUS MODE' : 'COMPARISON RESULT'} />}
  </Box>
)

const Result = ({ title, value, change }) => (
  <Box column alignItems="center" maxWidth={200} gap={16}>
    <H4 align="center">{title}</H4>
    <Expertise value={value} change={change} />
  </Box>
)

const Target = forwardRef(function Target({ title, values, selectedPractice, onPracticeSelected, transformOrigin }, ref) {
  const scale = 1 / useScale()

  return (
    <Box
      className="target"
      column
      alignItems="center"
      fullHeight
      scale={scale}
      transformOrigin={transformOrigin}
    >
      <H4>{title}</H4>

      {(values?.some(x => x))
        ? <PracticePie
            showAllHeadings
            values={values}
            selectedPractice={selectedPractice}
            onPracticeSelected={onPracticeSelected}
            ref={ref}
          />
        : <NoFilters />
      }
    </Box>
  )
})

const NoFilters = () => (
  <CenteredBox column aspect="1/1" fullHeight gap={16}>
    <img src={assetUrl('gifs/waiting.gif')} alt="no filters selected" style={{ width: 80 }} />
    <Body alt bold align="center">
      No data is available for<br/>the selected filters
    </Body>
  </CenteredBox>
)